import { useState } from 'react';
import type { MaterialItem } from '@/api/materialInfo';
import { submitFeedback, type FeedbackType } from '@/api/feedback';

interface FeedbackModalProps {
  material: MaterialItem;
  onClose: () => void;
  onSuccess?: () => void;
}

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: 'ERRATA', label: 'Errata' },
  { value: 'SUGGESTION', label: 'Suggestion' },
  { value: 'OTHER', label: 'Other' },
];

export default function FeedbackModal({ material, onClose, onSuccess }: FeedbackModalProps) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('OTHER');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    const trimmed = content.trim();
    if (trimmed.length < 1 || trimmed.length > 2000) {
      setError('Description Content must be 1–2000 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await submitFeedback({ materialId: material.id, content: trimmed, feedbackType });
      setSuccess(true);
      onSuccess?.();
      setTimeout(onClose, 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
        style={{ boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Feedback</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {success && (
            <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
              Feedback submitted.
            </div>
          )}

          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Type</label>
            <div className="space-y-2">
              {TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="radio"
                    name="feedbackType"
                    checked={feedbackType === opt.value}
                    onChange={() => setFeedbackType(opt.value)}
                    disabled={loading || success}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Material</label>
            <div className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 text-sm text-gray-600 truncate">
              {material.title}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              className="w-full min-h-[120px] px-3 py-2 border border-gray-200 rounded-lg text-sm resize-y outline-none focus:border-[#0069BD]"
              placeholder="Describe the issue or suggestion…"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={loading || success}
              maxLength={2000}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{content.trim().length}/2000</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || success}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer disabled:opacity-50"
              style={{ background: '#0069BD' }}
            >
              {loading ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
